import { Breadcrumbs, Button, Grid, Link, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { hasPermission, IfPermission } from "../../../components";
import Permissions from "../../../constants/permissions";
import { Role } from "../../../constants/roles";
import { UserDetailV2 } from "../../../graphql/types";

import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { ProhibitIcon } from "@phosphor-icons/react/dist/csr/Prohibit";
import { BaseAdminContainer } from "../../../components/ui/common";
import useAuth from "../../../context/UserContext";
import { AddUser } from "./AddUser";
import { DeactivateUser } from "./DeactivateUser";
import { EditableSelectRoles } from "./EditableSelectRoles/EditableSelectRoles";
import { Identities } from "./Identities";
import { UserAccountsTable } from "./UserAccountsTable";

const UserData = ({ user }: { user: UserDetailV2 }) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showDeactivateUserModal, setShowDeactivateUserModal] = useState(false);
  const [currentTab, setCurrentTab] = useState<"accounts" | "identities">("accounts");
  const { user: authenticatedUser } = useAuth();
  const hasGlobalRoles = user?.globalRoles && user.globalRoles.length > 0;

  return (
    <>
      <Breadcrumbs>
        <Link to="/admin?currentTab=users" component={RouterLink}>
          <Typography variant="body.overline.regular">Users</Typography>
        </Link>
        <Typography variant="body.overline.bold">{user.email}</Typography>
      </Breadcrumbs>
      <Stack spacing={2} sx={{ mt: 2 }}>
        <BaseAdminContainer>
          <Grid container>
            <Grid size={9} sx={{ height: "40px" }}>
              <Grid container sx={{ alignItems: "center" }}>
                <Typography variant="heading.four" data-testid="user-name-label">
                  {user.name}
                </Typography>
              </Grid>
            </Grid>
            <IfPermission permission={Permissions.ManageAccounts}>
              <Grid container size={3} sx={{ justifyContent: "flex-end", marginLeft: "auto" }}>
                <Button
                  color="error"
                  onClick={() => setShowDeactivateUserModal(true)}
                  data-testid="deactivate-user"
                  startIcon={<ProhibitIcon />}
                >
                  Deactivate
                </Button>
              </Grid>
            </IfPermission>
          </Grid>
          <Grid size={9} sx={{ height: "40px" }}>
            <Grid container sx={{ alignItems: "center" }}>
              <Typography variant="heading.six" data-testid="user-email-label" sx={{ color: "text30.main" }}>
                {user.email}
              </Typography>
            </Grid>
          </Grid>
          <Grid container size={{ xs: 12, md: 10, lg: 6 }} sx={{ alignItems: "center", mt: 1, minHeight: "35px" }}>
            {hasGlobalRoles ? (
              <Typography
                variant="body.medium.bold"
                data-testid="user-global-roles-label"
                sx={{ color: "text50.main" }}
              >
                Global Roles:
              </Typography>
            ) : null}
            {hasPermission(Permissions.ManageGlobalUsers, authenticatedUser) ? (
              <EditableSelectRoles
                roles={user.globalRoles as Role[]}
                email={user.email}
                showAddButtonText
                isGlobalSelector
              />
            ) : null}
          </Grid>
        </BaseAdminContainer>
        <Stack direction="row" sx={{ width: "100%", justifyContent: "center" }}>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} centered>
            <Tab value="accounts" label="Accounts" />
            <Tab value="identities" label="Identities" />
          </Tabs>
        </Stack>
      </Stack>
      {currentTab === "accounts" && (
        <BaseAdminContainer>
          <Stack sx={{ display: "flex", justifyContent: "space-between", marginBottom: "16px", flexDirection: "row" }}>
            <Typography variant="heading.four">
              {hasGlobalRoles ? "Additional Roles by Account" : "Roles by account"}
            </Typography>

            <Button
              sx={{ width: "fit-content" }}
              startIcon={<PlusIcon />}
              onClick={() => setShowAddUserModal(true)}
              aria-label="add-account-button"
            >
              Account
            </Button>
          </Stack>
          {hasGlobalRoles ? (
            <Typography variant="body.medium.regular">
              These are extra roles that apply only to the accounts below, on top of global roles.
            </Typography>
          ) : null}
          <UserAccountsTable
            accounts={user.accounts}
            userEmail={user.email}
            userName={user.name}
            globalRoles={(user.globalRoles as Role[]) || undefined}
          />
        </BaseAdminContainer>
      )}
      {currentTab === "identities" && <Identities email={user.email} identities={user.identities} />}
      <AddUser open={showAddUserModal} email={user.email} name={user.name} onClose={() => setShowAddUserModal(false)} />
      {showDeactivateUserModal ? (
        <DeactivateUser
          showModal={showDeactivateUserModal}
          closeModal={() => setShowDeactivateUserModal(false)}
          userName={user.name}
          userEmail={user.email}
        />
      ) : null}
    </>
  );
};
export { UserData };
